import type { WorkRow } from "../api/works";

export type ExportColumn = {
  header: string;
  accessor: (row: WorkRow) => string | number | null;
};

export const EXPORT_COLUMNS: ExportColumn[] = [
  { header: "created_at", accessor: (r) => r.created_at },
  { header: "title", accessor: (r) => r.title },
  { header: "type", accessor: (r) => r.type },
  { header: "format", accessor: (r) => r.format },
  { header: "status", accessor: (r) => r.status },
  { header: "profiles", accessor: (r) => r.profiles.join("; ") },
];

function csvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildCsv(rows: WorkRow[]): Blob {
  const headerLine = EXPORT_COLUMNS.map((c) => csvField(c.header)).join(",");
  const lines = rows.map((r) =>
    EXPORT_COLUMNS.map((c) => csvField(c.accessor(r))).join(","),
  );
  const content = [headerLine, ...lines].join("\r\n") + "\r\n";
  return new Blob([content], { type: "text/csv;charset=utf-8" });
}

export async function buildXlsx(rows: WorkRow[]): Promise<Blob> {
  // write-excel-file, not SheetJS: `xlsx` is frozen at 0.18.5 on npm (SheetJS
  // moved distribution to their own CDN) and carries two unpatchable high
  // advisories. Both are in the READER, which this app never used — but a
  // dependency that can never be patched is not one to keep. (86bbjv18u)
  //
  // The /browser entry is named explicitly: this package has no bare "." export,
  // and pinning the entry keeps test and bundle resolution identical.
  const writeXlsxFile = (await import("write-excel-file/browser")).default;
  const data = [
    EXPORT_COLUMNS.map((c) => ({ value: c.header, type: String })),
    ...rows.map((r) =>
      EXPORT_COLUMNS.map((c) => {
        const v = c.accessor(r);
        // A bare null is a valid empty cell; { value: null } is not — the Cell
        // union allows null itself but CellObject.value does not.
        if (v === null || v === undefined) return null;
        // Keep numbers numeric so they sort and aggregate in Excel rather than
        // landing as left-aligned text.
        return typeof v === "number"
          ? { value: v, type: Number }
          : { value: String(v), type: String };
      }),
    ),
  ];
  // No fileName argument, so the writer hands back { toBlob, toFile } rather
  // than triggering a download itself — this app owns the download step.
  const out = await (await writeXlsxFile(data, { sheet: "Works" })).toBlob();
  return new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportFilename(format: "csv" | "xlsx"): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `works-${stamp}.${format}`;
}
